import { useEffect } from 'react';
import { create } from 'zustand';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/api';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import type { ChatMessage } from '../api/conversations';
import { extractApiError, parseApiError } from '../utils/apiError';

let activeQueryClient: QueryClient | null = null;

export interface QueuedAttachment {
  resource_type: string;
  public_id: string;
  url: string;
  mime_type: string;
  file_size: number;
  name: string;
  original_filename?: string;
  duration_seconds?: number;
}

function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(0);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration || 0);
    };
    video.onerror = () => {
      resolve(0);
    };
    video.src = URL.createObjectURL(file);
  });
}

export interface QueuedMessage {
  localId: string;
  conversationUuid: string;
  body: string;
  status: 'pending' | 'uploading' | 'sending' | 'sent' | 'failed' | 'rate-limited';
  retryCount: number;
  createdAt: number;
  error?: string;
  file?: File;
  fileMimeType?: string;
  fileResourceType?: string;
  uploadProgress?: number;
  attachments?: QueuedAttachment[];
  serverMessage?: ChatMessage;
}

interface MessageQueueState {
  queue: QueuedMessage[];
  isProcessing: boolean;
  sentTimestamps: number[]; // Sliding window for rate limit tracking
  cooldownUntil: number; // Timestamp when cooldown ends

  enqueue: (msg: Omit<QueuedMessage, 'localId' | 'status' | 'retryCount' | 'createdAt'>) => string;
  updateMessage: (localId: string, updates: Partial<QueuedMessage>) => void;
  removeMessage: (localId: string) => void;
  retryMessage: (localId: string) => void;
  getConversationQueue: (conversationUuid: string) => QueuedMessage[];
  markSent: (localId: string, serverMessage: ChatMessage, queryClient?: QueryClient) => void;
  startProcessing: (queryClient?: QueryClient) => void;
  stopProcessing: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
const RATE_LIMIT_WINDOW_MS = 60000; // 60 seconds
const RATE_LIMIT_MAX = 60;
const RATE_LIMIT_SOFT_MAX = 55; // Start slowing down at 55
const COOLDOWN_BASE_MS = 3000;
const COOLDOWN_MAX_MS = 10000;

const isRateLimited = (error: unknown): boolean => {
  const parsed = parseApiError(error);
  return parsed.status === 429 || parsed.error_code === 'THROTTLED';
};

let processingTimer: ReturnType<typeof setTimeout> | null = null;

const activeProcessingLocalIds = new Set<string>();
const sentLocalIds = new Set<string>();

const processNext = () => {
  const state = useMessageQueueStore.getState();
  const { queue, cooldownUntil, sentTimestamps } = state;

  // Find first processable message that is not currently being processed or already sent
  const nextMsg = queue.find(
    (m) =>
      (m.status === 'pending' || m.status === 'rate-limited') &&
      !activeProcessingLocalIds.has(m.localId) &&
      !sentLocalIds.has(m.localId),
  );

  if (!nextMsg) {
    useMessageQueueStore.setState({ isProcessing: false });
    return;
  }

  // Check cooldown
  const now = Date.now();
  if (now < cooldownUntil) {
    const waitMs = cooldownUntil - now;
    if (processingTimer) clearTimeout(processingTimer);
    processingTimer = setTimeout(processNext, waitMs);
    return;
  }

  // Check sliding window rate limit
  const recentTimestamps = sentTimestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  if (recentTimestamps.length >= RATE_LIMIT_SOFT_MAX) {
    const delayMs = recentTimestamps.length >= RATE_LIMIT_MAX ? 2000 : 1000;
    if (processingTimer) clearTimeout(processingTimer);
    processingTimer = setTimeout(processNext, delayMs);
    return;
  }

  // Process the message
  processMessage(nextMsg.localId);
};

const processMessage = async (localId: string) => {
  if (activeProcessingLocalIds.has(localId) || sentLocalIds.has(localId)) {
    return;
  }

  const state = useMessageQueueStore.getState();
  const msg = state.queue.find((m) => m.localId === localId);
  if (!msg || msg.status === 'sent') {
    processNext();
    return;
  }

  activeProcessingLocalIds.add(localId);

  try {
    const payload: any = { body: msg.body || '' };

    // Handle file upload if present
    if (msg.file && !msg.attachments) {
      useMessageQueueStore
        .getState()
        .updateMessage(localId, { status: 'uploading', uploadProgress: 0 });

      try {
        const rawResourceType = msg.fileResourceType || 'auto';
        let sigResourceType: 'auto' | 'image' | 'video' | 'raw' | 'document' = 'auto';
        if (rawResourceType === 'image') {
          sigResourceType = 'image';
        } else if (rawResourceType === 'video' || rawResourceType === 'audio') {
          sigResourceType = 'video';
        } else if (rawResourceType === 'document') {
          sigResourceType = 'document';
        } else if (rawResourceType === 'raw') {
          sigResourceType = 'raw';
        }

        const results = await uploadFilesToCloudinary([msg.file], {
          folder: 'chat_attachments',
          uploadPreset: 'service_preset',
          resourceType: sigResourceType,
          onProgress: (percent) => {
            useMessageQueueStore.getState().updateMessage(localId, { uploadProgress: percent });
          },
        });

        if (results.length > 0) {
          const result = results[0];
          const isPdf = msg.file?.name.toLowerCase().endsWith('.pdf');
          const isVideo = (msg.fileResourceType || result.resource_type) === 'video';
          let duration_seconds: number | undefined = undefined;
          if (isVideo) {
            const rawDuration = (result as any).duration || 0;
            duration_seconds = Math.round(rawDuration);
            if (!duration_seconds && msg.file) {
              try {
                const rawFileDuration = await getVideoDuration(msg.file);
                duration_seconds = Math.round(rawFileDuration);
              } catch (e) {
                console.error('Failed to get video duration:', e);
              }
            }
          }

          const attachments: QueuedAttachment[] = [
            {
              resource_type:
                msg.fileResourceType || (isPdf ? 'document' : result.resource_type) || 'document',
              public_id: result.public_id,
              url: result.secure_url,
              mime_type: msg.fileMimeType || 'application/octet-stream',
              file_size: result.bytes || msg.file.size,
              name: msg.file.name,
              original_filename: msg.file.name,
              ...(duration_seconds !== undefined ? { duration_seconds } : {}),
            },
          ];
          useMessageQueueStore.getState().updateMessage(localId, {
            attachments,
            uploadProgress: 100,
          });

          payload.attachments = attachments;
          if (!msg.body?.trim()) {
            payload.body = `Shared a file: ${msg.file.name}`;
          }
        }
      } catch (uploadErr) {
        console.error('Queue: Cloudinary upload failed:', uploadErr);
        const currentMsg = useMessageQueueStore.getState().queue.find((m) => m.localId === localId);
        const retryCount = (currentMsg?.retryCount || 0) + 1;
        if (retryCount >= MAX_RETRIES) {
          useMessageQueueStore.getState().updateMessage(localId, {
            status: 'failed',
            error: 'Failed to upload file',
            retryCount,
          });
        } else {
          useMessageQueueStore.getState().updateMessage(localId, {
            status: 'pending',
            retryCount,
          });
          setTimeout(processNext, RETRY_DELAY_MS);
        }
        return;
      }
    } else if (msg.attachments) {
      payload.attachments = msg.attachments;
      if (!msg.body?.trim()) {
        payload.body = `Shared a file: ${msg.attachments[0]?.original_filename || msg.attachments[0]?.name || 'file'}`;
      }
    }

    // Send the message
    useMessageQueueStore.getState().updateMessage(localId, { status: 'sending' });

    const { data } = await apiClient.post<any>(
      `/conversations/${msg.conversationUuid}/messages/`,
      payload,
    );
    const serverMessage = (data?.data || data) as ChatMessage;

    // Success
    sentLocalIds.add(localId);
    useMessageQueueStore.setState((s) => ({
      sentTimestamps: [...s.sentTimestamps, Date.now()].filter(
        (t) => Date.now() - t < RATE_LIMIT_WINDOW_MS,
      ),
    }));
    useMessageQueueStore
      .getState()
      .markSent(localId, serverMessage, activeQueryClient || undefined);

    // Natural pacing before next message
    setTimeout(processNext, 150);
  } catch (err: any) {
    console.error('Queue: Send failed:', err);

    if (isRateLimited(err)) {
      const currentMsg = useMessageQueueStore.getState().queue.find((m) => m.localId === localId);
      const retryCount = currentMsg?.retryCount || 0;
      const cooldownMs = Math.min(COOLDOWN_BASE_MS * Math.pow(1.5, retryCount), COOLDOWN_MAX_MS);

      useMessageQueueStore.setState({ cooldownUntil: Date.now() + cooldownMs });
      useMessageQueueStore.getState().updateMessage(localId, {
        status: 'rate-limited',
        retryCount: retryCount + 1,
      });

      if (processingTimer) clearTimeout(processingTimer);
      processingTimer = setTimeout(processNext, cooldownMs);
      return;
    }

    const currentMsg = useMessageQueueStore.getState().queue.find((m) => m.localId === localId);
    const retryCount = (currentMsg?.retryCount || 0) + 1;

    if (retryCount >= MAX_RETRIES) {
      useMessageQueueStore.getState().updateMessage(localId, {
        status: 'failed',
        error: extractApiError(err, 'Failed to send'),
        retryCount,
      });
      setTimeout(processNext, 100);
    } else {
      useMessageQueueStore.getState().updateMessage(localId, {
        status: 'pending',
        retryCount,
      });
      setTimeout(processNext, RETRY_DELAY_MS * retryCount);
    }
  } finally {
    activeProcessingLocalIds.delete(localId);
  }
};

export const useMessageQueueStore = create<MessageQueueState>((set, get) => ({
  queue: [],
  isProcessing: false,
  sentTimestamps: [],
  cooldownUntil: 0,

  enqueue: (msg) => {
    const localId = crypto.randomUUID();
    const queuedMsg: QueuedMessage = {
      ...msg,
      localId,
      status: 'pending',
      retryCount: 0,
      createdAt: Date.now(),
    };

    set((state) => ({
      queue: [...state.queue, queuedMsg],
    }));

    // Auto-start processing if not already running
    if (!get().isProcessing) {
      get().startProcessing();
    }

    return localId;
  },

  updateMessage: (localId, updates) => {
    set((state) => ({
      queue: state.queue.map((m) => (m.localId === localId ? { ...m, ...updates } : m)),
    }));
  },

  removeMessage: (localId) => {
    set((state) => ({
      queue: state.queue.filter((m) => m.localId !== localId),
    }));
  },

  retryMessage: (localId) => {
    set((state) => ({
      queue: state.queue.map((m) =>
        m.localId === localId
          ? { ...m, status: 'pending' as const, retryCount: 0, error: undefined }
          : m,
      ),
    }));

    if (!get().isProcessing) {
      get().startProcessing();
    }
  },

  getConversationQueue: (conversationUuid) => {
    return get().queue.filter(
      (m) => m.conversationUuid === conversationUuid && m.status !== 'sent',
    );
  },

  markSent: (localId, serverMessage, queryClient) => {
    const qm = get().queue.find((m) => m.localId === localId);
    const convUuid = qm?.conversationUuid || serverMessage.conversation_id;

    // Use initial client creation timestamp so message position NEVER jumps
    const createdAtIso = qm?.createdAt
      ? new Date(qm.createdAt).toISOString()
      : serverMessage.created_at || new Date().toISOString();

    const enhancedServerMsg: ChatMessage & { localId?: string } = {
      ...serverMessage,
      localId,
      created_at: createdAtIso,
    };

    // Remove from queue immediately — no 2000ms ghost window causing dual items
    set((state) => ({
      queue: state.queue.filter((m) => m.localId !== localId),
    }));

    // Update query cache for current conversation if queryClient available
    if (queryClient && convUuid) {
      queryClient.setQueryData<any>(['conversation-messages', convUuid], (old: any) => {
        if (!old) return old;
        const hasMsg = old.pages?.some((page: any) => {
          const results = Array.isArray(page)
            ? page
            : page?.messages || page?.data || page?.results || [];
          return results.some(
            (m: any) => m.id === enhancedServerMsg.id || (m.localId && m.localId === localId),
          );
        });
        if (hasMsg) return old;

        const newPages = [...old.pages];
        if (newPages.length > 0) {
          const firstPage = newPages[0];
          if (Array.isArray(firstPage)) {
            newPages[0] = [enhancedServerMsg, ...firstPage];
          } else if (firstPage && Array.isArray(firstPage.messages)) {
            newPages[0] = { ...firstPage, messages: [enhancedServerMsg, ...firstPage.messages] };
          } else if (firstPage && Array.isArray(firstPage.data)) {
            newPages[0] = { ...firstPage, data: [enhancedServerMsg, ...firstPage.data] };
          } else if (firstPage && Array.isArray(firstPage.results)) {
            newPages[0] = { ...firstPage, results: [enhancedServerMsg, ...firstPage.results] };
          }
        }
        return { ...old, pages: newPages };
      });

      // Update conversations list snippet & re-sort
      queryClient.setQueryData<any[]>(['conversations'], (old = []) => {
        let exists = false;
        const updated = old.map((conv) => {
          if (conv.conversation_uuid === convUuid) {
            exists = true;
            return {
              ...conv,
              last_message: enhancedServerMsg,
              last_message_text: enhancedServerMsg.body,
              last_message_time: enhancedServerMsg.created_at,
              updated_at: enhancedServerMsg.created_at,
            };
          }
          return conv;
        });

        if (!exists) {
          const detail = queryClient.getQueryData<any>(['conversation', convUuid]);
          if (detail) {
            const newItem = {
              ...detail,
              last_message: enhancedServerMsg,
              last_message_text: enhancedServerMsg.body,
              last_message_time: enhancedServerMsg.created_at,
              updated_at: enhancedServerMsg.created_at,
            };
            updated.unshift(newItem);
          } else {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
          }
        }

        return [...updated].sort((a, b) => {
          const tA = new Date(a.updated_at || a.last_message_time || 0).getTime();
          const tB = new Date(b.updated_at || b.last_message_time || 0).getTime();
          return tB - tA;
        });
      });
    }
  },

  startProcessing: () => {
    set({ isProcessing: true });
    processNext();
  },

  stopProcessing: () => {
    set({ isProcessing: false });
    if (processingTimer) {
      clearTimeout(processingTimer);
      processingTimer = null;
    }
  },
}));

/** Hook for components to interact with the message queue */
export function useMessageQueue(conversationUuid?: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    activeQueryClient = queryClient;
  }, [queryClient]);
  const store = useMessageQueueStore();

  const queuedMessages = conversationUuid
    ? store.queue.filter((m) => m.conversationUuid === conversationUuid && m.status !== 'sent')
    : [];

  const enqueue = (
    body: string,
    options?: {
      file?: File;
      fileMimeType?: string;
      fileResourceType?: string;
    },
  ) => {
    if (!conversationUuid) return null;
    return store.enqueue({
      conversationUuid,
      body,
      file: options?.file,
      fileMimeType: options?.fileMimeType,
      fileResourceType: options?.fileResourceType,
    });
  };

  return {
    queuedMessages,
    enqueue,
    retryMessage: store.retryMessage,
    removeMessage: store.removeMessage,
    isProcessing: store.isProcessing,
  };
}
